import { motion } from "framer-motion";
import { BRAND, INSTAGRAM_REELS } from "@/site.config";

// Extracts the last path segment from an Instagram URL if it looks like a reel/post,
// so we can render the native embed iframe. If not, we render a poster card.
function reelId(url) {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(reel|p|tv)\/([^/?]+)/i);
  return m ? { kind: m[1], id: m[2] } : null;
}

export default function Reels() {
  return (
    <section
      id="reels"
      data-testid="reels-section"
      className="relative py-20 md:py-40 bg-[#050505]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-16 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              Ch. 07 &mdash; Field Notes
            </div>
            <h2 className="font-display text-white text-[34px] sm:text-4xl md:text-6xl lg:text-[80px] leading-[0.98] tracking-[-0.02em]">
              Reels from{" "}
              <em className="font-editorial italic text-[#D4AF37] font-light">
                the floor.
              </em>
            </h2>
          </div>
          <div className="lg:col-span-4 text-white/60 text-sm md:text-base leading-relaxed lg:pl-6">
            Short, honest takes on venues, guests, menus, staff and margin —
            straight from behind the console.
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="reels-instagram-link"
              className="mt-4 inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#f3d568] transition-colors"
            >
              Follow {BRAND.instagramHandle}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 17L17 7M17 7H8M17 7v9"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
              </svg>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {INSTAGRAM_REELS.map((r, i) => {
            const rid = reelId(r.url);
            return (
              <motion.a
                key={i}
                href={r.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`reel-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="group relative aspect-[9/16] overflow-hidden rounded-xl border border-white/8 bg-black block"
              >
                {rid ? (
                  <iframe
                    title={`Instagram ${rid.kind} ${rid.id}`}
                    src={`https://www.instagram.com/${rid.kind}/${rid.id}/embed`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    scrolling="no"
                    allowtransparency="true"
                    allow="encrypted-media"
                    loading="lazy"
                  />
                ) : (
                  <>
                    <img
                      src={r.poster}
                      alt={r.caption}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                      Reel
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#D4AF37]/90 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="black">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-white text-sm leading-snug">
                        {r.caption}
                      </div>
                      <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                        Watch on Instagram
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M7 17L17 7M17 7H8M17 7v9"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                    </div>
                  </>
                )}
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
